/**
 * contactSync.ts
 *
 * Orchestrates the real-time RTDB channel between the web app and the Flutter
 * app. This service is stateless — all persistent state lives in IndexedDB
 * (contactSyncStorage.ts) and Pinia (contactStore.ts).
 *
 * Responsibilities:
 *   - Subscribe to the RTDB sync channel and decrypt incoming payloads.
 *   - Write a `syncRequestedAt` timestamp to RTDB to request a manual sync.
 *   - Delete the RTDB channel node on unlink.
 */

import {
  ref as dbRef,
  onValue,
  off,
  set,
  remove,
  serverTimestamp,
  type Database,
} from 'firebase/database';

import { deriveAesKey, decryptPayload, type EncryptedPayload } from './contactSyncCrypto';
import { writeCachedContacts } from './contactSyncStorage';
import type { SyncedContact } from '~/types/contacts';

export interface ChannelSnapshot {
  mobilePublicKey: string;
  encryptedPayload: string; // JSON string of EncryptedPayload
  lastUpdated: number;
}

type OnContactsUpdate = (contacts: SyncedContact[]) => void;
type OnError = (err: unknown) => void;
type OnRevoked = () => void;

// ── RTDB Helpers ──────────────────────────────────────────────────────────────

function channelRef(db: Database, uid: string, deviceId: string) {
  return dbRef(db, `users/${uid}/sync_channels/${deviceId}`);
}

// ── Subscription ──────────────────────────────────────────────────────────────

/**
 * Subscribes to the RTDB sync channel for [deviceId].
 *
 * Whenever the mobile app pushes a new encrypted payload:
 *   1. Derives the AES key using ECDH(webPrivKey, mobilePublicKey).
 *   2. Decrypts the payload with AES-256-GCM.
 *   3. Parses the contacts JSON and calls [onUpdate].
 *   4. Persists decrypted contacts to IndexedDB for offline access.
 *
 * Returns an unsubscribe function. Call it when navigating away or on unlink.
 */
export function watchSyncChannel(
  db: Database,
  uid: string,
  deviceId: string,
  webPrivateKey: CryptoKey,
  onUpdate: OnContactsUpdate,
  onError: OnError,
  onRevoked?: OnRevoked,
): () => void {
  const ref = channelRef(db, uid, deviceId);

  // Firebase fires onValue immediately on subscription — if the channel path
  // doesn't exist yet the snapshot is null. We must not treat that initial
  // null as a deletion; only a null that arrives AFTER real data counts.
  let hasReceivedData = false;

  onValue(
    ref,
    async (snapshot) => {
      const data = snapshot.val() as ChannelSnapshot | null;

      if (data === null) {
        // Only treat null as a revocation once real data has been seen.
        // The very first null simply means the channel doesn't exist yet.
        if (hasReceivedData) {
          onRevoked?.();
        }
        return;
      }

      hasReceivedData = true;

      if (!data.encryptedPayload || !data.mobilePublicKey) return;

      try {
        const payload = JSON.parse(data.encryptedPayload) as EncryptedPayload;
        const aesKey = await deriveAesKey(
          webPrivateKey,
          data.mobilePublicKey,
          deviceId,
        );
        const plaintext = await decryptPayload(aesKey, payload);
        const contacts = JSON.parse(plaintext) as SyncedContact[];

        await writeCachedContacts(contacts);
        onUpdate(contacts);
      } catch (err) {
        console.error('[contactSync] Failed to decrypt payload', err);
        onError(err);
      }
    },
    (err) => {
      console.error('[contactSync] RTDB listener error', err);
      onError(err);
    },
  );

  return () => off(ref);
}

// ── Manual Sync Request ───────────────────────────────────────────────────────

/**
 * Sets `syncRequestedAt` on the RTDB channel to a server timestamp.
 * The Flutter app's RTDB listener detects this and pushes a fresh payload.
 */
export async function requestManualSync(
  db: Database,
  uid: string,
  deviceId: string,
): Promise<void> {
  const ref = dbRef(db, `users/${uid}/sync_channels/${deviceId}/syncRequestedAt`);
  await set(ref, serverTimestamp());
}

// ── Unlink ────────────────────────────────────────────────────────────────────

/**
 * Removes the entire RTDB channel node for [deviceId].
 * This revokes the mobile app's ability to push future payloads to this web session.
 */
export async function deleteChannel(
  db: Database,
  uid: string,
  deviceId: string,
): Promise<void> {
  await remove(channelRef(db, uid, deviceId));
}
